import {
  SalesRecording, SalesRecordingStatus, TripMatch, TripMatchStatus, TripRequestStatus,
} from '@marageti/z4-sdk/lib/travel';
import { SalesRecordingType } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { isSameMonth } from 'date-fns';
import { Agent } from '@marageti/z4-sdk/lib/people';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { canAddSalesAdjustment } from '../../utils/sales';

export enum AlertType {
  FirstMessageReminder = 'FirstMessageReminder',
  DepositConfirmation = 'DepositConfirmation',
  SalesRecordingEdit = 'SalesRecordingEdit',
  SalesAdjustment = 'SalesAdjustment',
  SalesAdjustmentEdit = 'SalesAdjustmentEdit',
  Postponed = 'Postponed',
  Inactive = 'Inactive',
  MatchSoldCanceled = 'MatchSoldCanceled',
  MatchRefundedCanceled = 'MatchRefundedCanceled',
  MatchTravelerCanceled = 'MatchTravelerCanceled',
}

// The same trip match resolves to a different alert depending on where it renders: the Sales tab does
// not render cancellation banners, so the Add Adjustment CTA (active SOLD trips only) takes priority
// there, while Details shows the cancellation banner. Canceled trips never show the Add Adjustment CTA.
export enum AlertPage {
  Details = 'Details',
  Sales = 'Sales',
}

export type AlertObj = {
  type: AlertType;
  url?: string | undefined;
  createdAt?: string | undefined;
  handleReactivation?: () => void;
  reason?: string | undefined;
};

export const createAlertObj = (
  tripMatch: TripMatch | undefined,
  salesRecordings: SalesRecording[] | undefined,
  agent: Agent | null,
  handleReactivation?: () => void,
  tripRequestStatus?: TripRequestStatus,
  page: AlertPage = AlertPage.Details,
): AlertObj | undefined => {
  if (!tripMatch) return undefined;
  const {
    id: tripMatchId, status: tripStatus, createdAt, travelerToPartnerId, statusInfo,
  } = tripMatch;

  const standardSale = salesRecordings?.find(
    (sale) => sale.salesRecordingType === SalesRecordingType.General,
  );

  const commissionAfterTravel = salesRecordings?.find(
    (sale) => sale.salesRecordingType === SalesRecordingType.CommissionAfterTravel,
  );

  const uninvoicedSalesAdjustment = salesRecordings?.find(
    (sale) => sale.salesRecordingType === SalesRecordingType.Adjustment
      && sale.status === SalesRecordingStatus.SoldStatus,
  );
  const isAgentNotSupport = agent?.userInfo.some((userInfo) => !userInfo.roles?.includes(RoleName.AGENT_SUPPORT));

  // 1. FirstMessageReminder
  if (
    tripStatus === TripMatchStatus.MatchAccepted
    && isAgentNotSupport
  ) {
    return {
      type: AlertType.FirstMessageReminder,
      url: `/trip-matches/${tripMatchId}/messages/${travelerToPartnerId}`,
      createdAt,
    };
  }

  // 2. Postponed
  if (tripStatus === TripMatchStatus.MatchPostponed) {
    // Remove the Reactivate banner when the parent Trip Request has been canceled.
    if (tripRequestStatus === TripRequestStatus.CanceledStatus) return undefined;
    return {
      type: AlertType.Postponed,
      handleReactivation,
    };
  }

  // 3. Inactive
  if (tripStatus === TripMatchStatus.MatchInactive) {
    // Remove the Reactivate banner when the parent Trip Request has been canceled.
    if (tripRequestStatus === TripRequestStatus.CanceledStatus) return undefined;
    return {
      type: AlertType.Inactive,
      handleReactivation,
    };
  }

  // 4. DepositConfirmation
  // If tripMatch status is ACTIVE and there is no standard sale.
  if (tripStatus === TripMatchStatus.MatchActive && !standardSale && !commissionAfterTravel) {
    return {
      type: AlertType.DepositConfirmation,
      url: `/trip-matches/${tripMatchId}/sales/recording`,
    };
  }

  // 5. Edit SalesRecording
  // If a single standard sales recording exists with status SOLD, and tripMatch status is SOLD, and the sales recording was created this month.
  if (
    standardSale
    && standardSale.status === SalesRecordingStatus.SoldStatus
    && tripStatus === TripMatchStatus.MatchSold
    && isSameMonth(new Date(standardSale.createdAt), new Date())
  ) {
    return {
      type: AlertType.SalesRecordingEdit,
      url: `/trip-matches/${tripMatchId}/sales/recording/${standardSale.id}`,
    };
  }

  // 6. Edit SalesAdjustment
  // If tripMatch status is SOLD and a sales recording with type ADJUSTMENT and status SOLD exists.
  if (tripStatus === TripMatchStatus.MatchSold && uninvoicedSalesAdjustment) {
    return {
      type: AlertType.SalesAdjustmentEdit,
      url: `/trip-matches/${tripMatchId}/sales/adjustment/${uninvoicedSalesAdjustment.id}`,
    };
  }

  const showAdjustment = canAddSalesAdjustment(tripMatch, salesRecordings);

  // 7. Create SalesAdjustment (Sales tab)
  // The Add Adjustment CTA only shows for active SOLD trips (canAddSalesAdjustment excludes canceled
  // trips). On the Sales tab it takes priority since that tab does not render cancellation banners.
  if (page === AlertPage.Sales && showAdjustment) {
    return {
      type: AlertType.SalesAdjustment,
      url: `/trip-matches/${tripMatchId}/sales/adjustment`,
    };
  }

  // 8. MatchSoldCanceled
  // Checked before the Details-side SalesAdjustment so Details shows the canceled banner + comments.
  if (tripStatus === TripMatchStatus.MatchSoldCanceled) {
    return { type: AlertType.MatchSoldCanceled, reason: statusInfo?.reason };
  }

  // 9. MatchRefundedCanceled
  if (tripStatus === TripMatchStatus.MatchRefundedCanceled) {
    return { type: AlertType.MatchRefundedCanceled, reason: statusInfo?.reason };
  }

  // 10. MatchTravelerCanceled
  // Also prevents the "Confirm Sold Trip" (DepositConfirmation) fallback below from showing on a
  // traveler-canceled match with no standard sale.
  if (tripStatus === TripMatchStatus.MatchTravelerCanceled) {
    return { type: AlertType.MatchTravelerCanceled };
  }

  // 11. Create SalesAdjustment (Details / sidebar)
  // Non-canceled SOLD trips only: canceled statuses returned their banner above, and the Sales tab
  // was handled in step 7.
  if (showAdjustment) {
    return {
      type: AlertType.SalesAdjustment,
      url: `/trip-matches/${tripMatchId}/sales/adjustment`,
    };
  }

  // 12. DepositConfirmation (fallback)
  // If there is no standard sale and no other specific conditions are met, show the create button.
  if (!standardSale) {
    return {
      type: AlertType.DepositConfirmation,
      url: `/trip-matches/${tripMatchId}/sales/recording`,
    };
  }

  return undefined;
};
